import {
  AddCircleOutlined,
  BusinessOutlined,
  CalendarTodayOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
  DescriptionOutlined,
  ErrorOutlineOutlined,
  ExpandMoreOutlined,
  InventoryOutlined,
  RefreshOutlined,
  SendOutlined,
} from '@mui/icons-material'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material'
import { DeleteButtonWithConfirmation } from 'components/Common/Button/DeleteButtonWithConfirmation'
import { useLocalizedWorkingStepLabel } from 'components/Localization/useLocalizedWorkingStepLabel'
import { useAppController } from 'customHooks/useAppController'
import { useClientStorage } from 'customHooks/useClientStorage'
import { useDialogManager } from 'features/DialogManager/useDialogManager'
import { ProjectSuppliersController } from 'features/ProjectSuppliers/ProjectSuppliersController'
import { BomItemPointer } from 'model/Project/BomItemPointer'
import moment from 'moment'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  MoneyDto,
  RequestForQuotationDto,
  RequestForQuotationState,
  WorkingStepType,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import { makeStyles } from 'tss-react/mui'
import { useShowException } from 'utils/useShowException'
import { ProjectRFQLine } from '../Components/ProjectRFQLine'
import { RFQLine } from '../Components/ProjectRFQLines'
import { ProjectRFQController } from '../ProjectRFQController'
import { RFQResponse } from './RFQResponse'

export type ManageRFQDialogProps = {
  isOpen: boolean
  onClose: () => void
  initialRequestForQuotationId?: string
}

const useStyles = makeStyles({ name: 'ManageRFQDialog' })((theme) => ({
  rfqCard: {
    padding: theme.spacing(2),
    borderRadius: `calc(${theme.shape.borderRadius}px * 2)`,
    border: `1px solid ${theme.palette.divider}`,
    transition: 'border-color 0.2s, box-shadow 0.2s',
    '&:hover': {
      borderColor: theme.palette.primary.main,
      boxShadow: `0 0 0 1px ${theme.palette.primary.main}20`,
    },
  },
  rfqCardHighlighted: {
    borderColor: theme.palette.info.main,
    boxShadow: `0 0 0 1px ${theme.palette.info.main}`,
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    width: '100%',
  },
  cardTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    width: '100%',
  },
  cardBody: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(1.5),
  },
  infoRow: {
    display: 'flex',
    gap: theme.spacing(2),
    flexWrap: 'wrap' as const,
    alignItems: 'center',
  },
  infoItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    color: theme.palette.text.secondary,
  },
  activitiesRow: {
    display: 'flex',
    gap: theme.spacing(0.5),
    flexWrap: 'wrap' as const,
    alignItems: 'center',
  },
  expandToggle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    cursor: 'pointer',
    userSelect: 'none',
    color: theme.palette.text.secondary,
    '&:hover': {
      color: theme.palette.text.primary,
    },
  },
  expandIcon: {
    transition: 'transform 200ms',
  },
  expandIconOpen: {
    transform: 'rotate(180deg)',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(6),
    color: theme.palette.text.secondary,
  },
  groupHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 0),
    cursor: 'pointer',
    userSelect: 'none',
    '&:hover': {
      color: theme.palette.primary.main,
    },
  },
  groupSection: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: theme.spacing(1.5),
  },
  summaryStrip: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    gap: theme.spacing(1),
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    },
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
    },
  },
  summaryCard: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1.25, 1.5),
    backgroundColor: theme.palette.background.paper,
  },
  summaryValue: {
    display: 'flex',
    alignItems: 'baseline',
    gap: theme.spacing(0.75),
  },
  cardActionsRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'stretch',
    },
  },
  cardPrimaryAction: {
    display: 'flex',
    justifyContent: 'flex-start',
  },
  cardUtilityActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(1.5),
  },
  cardSection: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
    backgroundColor: theme.palette.background.paper,
  },
  cardSectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1.25, 1.5),
    backgroundColor: theme.palette.background.default,
  },
  cardSectionTitle: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: theme.spacing(0.25),
  },
}))

function getStatusColor(
  status: string | undefined,
): 'success' | 'error' | 'warning' | 'info' | 'default' {
  switch (status) {
    case RequestForQuotationState.New:
    case RequestForQuotationState.DocumentCreated:
      return 'info'
    case RequestForQuotationState.RequestForQuotationSent:
    case RequestForQuotationState.Parsed:
      return 'warning'
    case RequestForQuotationState.ProjectCreated:
    case RequestForQuotationState.Accepted:
    case RequestForQuotationState.QuotationResponseSent:
      return 'success'
    case RequestForQuotationState.RequestForQuotationReceivedError:
    case RequestForQuotationState.ParsedError:
    case RequestForQuotationState.ProjectCreatedError:
    case RequestForQuotationState.QuotationResponseSentError:
      return 'error'
    default:
      return 'default'
  }
}

function canSend(status: string | undefined): boolean {
  return (
    status === RequestForQuotationState.New ||
    status === RequestForQuotationState.DocumentCreated
  )
}

function formatMoney(money: MoneyDto | undefined): string {
  if (!money?.value && money?.value !== 0) return '-'
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: money.currency || 'EUR',
    }).format(money.value)
  } catch {
    return `${money.value} ${money.currency || ''}`
  }
}

type GroupBy = 'none' | 'supplier' | 'activity' | 'status'
type RfqView = 'needsAction' | 'all' | 'awaiting' | 'completed'

type RFQGroup = {
  key: string
  label: string
  rfqs: RequestForQuotationDto[]
}

function groupRFQs(
  rfqs: RequestForQuotationDto[],
  groupBy: GroupBy,
  supplierMap: Record<string, string>,
  getLocalizedWorkingStepName: (ws: WorkingStepType) => string,
): RFQGroup[] {
  if (groupBy === 'none') {
    return [{ key: 'all', label: '', rfqs }]
  }

  const groups = new Map<string, RequestForQuotationDto[]>()

  if (groupBy === 'supplier') {
    for (const rfq of rfqs) {
      const supplierId = rfq.forwardId ?? 'unassigned'
      const existing = groups.get(supplierId) ?? []
      existing.push(rfq)
      groups.set(supplierId, existing)
    }

    return Array.from(groups.entries()).map(([key, rfqList]) => ({
      key,
      label: key === 'unassigned' ? 'No Supplier' : supplierMap[key] || key,
      rfqs: rfqList,
    }))
  }

  if (groupBy === 'status') {
    for (const rfq of rfqs) {
      const status = rfq.status || 'Unknown'
      const existing = groups.get(status) ?? []
      existing.push(rfq)
      groups.set(status, existing)
    }

    return Array.from(groups.entries()).map(([key, rfqList]) => ({
      key,
      label: key,
      rfqs: rfqList,
    }))
  }

  // group by activity
  for (const rfq of rfqs) {
    const steps = getUniqueWorkingSteps(rfq.bomItemsAndActivities)
    if (steps.length === 0) {
      const existing = groups.get('none') ?? []
      existing.push(rfq)
      groups.set('none', existing)
    } else {
      for (const ws of steps) {
        const existing = groups.get(ws) ?? []
        existing.push(rfq)
        groups.set(ws, existing)
      }
    }
  }

  return Array.from(groups.entries()).map(([key, rfqList]) => ({
    key,
    label:
      key === 'none'
        ? 'No Activities'
        : getLocalizedWorkingStepName(key as WorkingStepType),
    rfqs: rfqList,
  }))
}

function getUniqueWorkingSteps(
  bomItemsAndActivities:
    | { activities?: { workingStepType?: WorkingStepType }[] | undefined }[]
    | undefined,
): WorkingStepType[] {
  if (!bomItemsAndActivities) return []
  const steps = new Set<WorkingStepType>()
  bomItemsAndActivities.forEach((item) =>
    item.activities?.forEach((a) => {
      if (a.workingStepType) steps.add(a.workingStepType)
    }),
  )
  return Array.from(steps)
}

function hasQuotationResponse(rfq: RequestForQuotationDto): boolean {
  return Boolean(
    rfq.quotationResponseId &&
    rfq.quotationResponseId !== '00000000-0000-0000-0000-000000000000',
  )
}

function isCompletedRFQ(rfq: RequestForQuotationDto): boolean {
  return (
    rfq.status === RequestForQuotationState.Accepted ||
    rfq.status === RequestForQuotationState.RequestForQuotationAccepted ||
    rfq.status === RequestForQuotationState.ProjectCreated
  )
}

function needsBuyerAction(rfq: RequestForQuotationDto): boolean {
  return (
    canSend(rfq.status) || (hasQuotationResponse(rfq) && !isCompletedRFQ(rfq))
  )
}

function isAwaitingSupplier(rfq: RequestForQuotationDto): boolean {
  return (
    !hasQuotationResponse(rfq) &&
    (rfq.status === RequestForQuotationState.RequestForQuotationSent ||
      rfq.status === RequestForQuotationState.QuotationResponseSent)
  )
}

function getRfqActionLabel(rfq: RequestForQuotationDto): {
  label: string
  helper: string
  color: 'success' | 'error' | 'warning' | 'info' | 'default'
} {
  if (needsBuyerAction(rfq)) {
    if (canSend(rfq.status)) {
      return {
        label: 'Ready to send',
        helper: 'Send this RFQ to request a supplier response',
        color: 'warning',
      }
    }

    return {
      label: 'Action required',
      helper: 'Supplier response ready for review',
      color: 'warning',
    }
  }

  if (isCompletedRFQ(rfq)) {
    return {
      label: 'Accepted',
      helper: 'A supplier response was accepted for this RFQ',
      color: 'success',
    }
  }

  if (isAwaitingSupplier(rfq)) {
    return {
      label: 'Awaiting supplier',
      helper: 'RFQ sent, no response received yet',
      color: 'info',
    }
  }

  if (getStatusColor(rfq.status) === 'error') {
    return {
      label: 'Needs attention',
      helper: 'Resolve the RFQ error before continuing',
      color: 'error',
    }
  }

  return {
    label: 'In progress',
    helper: 'No buyer decision is available yet',
    color: 'default',
  }
}

function getActionIconColor(
  color: ReturnType<typeof getRfqActionLabel>['color'],
): 'success' | 'error' | 'warning' | 'info' | 'action' {
  return color === 'default' ? 'action' : color
}

function filterRFQs(rfqs: RequestForQuotationDto[], view: RfqView) {
  switch (view) {
    case 'needsAction':
      return rfqs.filter(needsBuyerAction)
    case 'awaiting':
      return rfqs.filter(isAwaitingSupplier)
    case 'completed':
      return rfqs.filter(isCompletedRFQ)
    default:
      return rfqs
  }
}

const RFQCard = ({
  rfq,
  loading,
  onSend,
  supplierName,
  onDelete,
  onActionComplete,
  initialExpanded,
  highlighted,
}: {
  rfq: RequestForQuotationDto
  loading: Record<string, boolean>
  onSend: (rfq: RequestForQuotationDto) => void
  supplierName: string | undefined
  onDelete: (rfq: RequestForQuotationDto) => void
  onActionComplete: () => Promise<void> | void
  initialExpanded?: boolean
  highlighted?: boolean
}) => {
  const { classes, cx } = useStyles()
  const getLocalizedWorkingStepName = useLocalizedWorkingStepLabel()
  const [expanded, setExpanded] = useState(initialExpanded ?? true)

  useEffect(() => {
    if (initialExpanded) {
      setExpanded(true)
    }
  }, [initialExpanded])

  const rfqData = rfq
  const sendLoading = loading[`sendRequestForQuotation-${rfqData.id}`] || false

  const workingSteps = useMemo(
    () => getUniqueWorkingSteps(rfqData.bomItemsAndActivities),
    [rfqData.bomItemsAndActivities],
  )

  const bomItemCount = rfqData.bomItemsAndActivities?.length ?? 0
  const hasResponse = hasQuotationResponse(rfqData)
  const actionState = getRfqActionLabel(rfqData)

  const rfqLines: RFQLine[] = useMemo(
    () =>
      (rfqData.bomItemsAndActivities ?? []).map((item) => ({
        bomItemPointer: { id: item.bomItemId } as BomItemPointer,
        activities: item.activities ?? [],
      })),
    [rfqData.bomItemsAndActivities],
  )

  return (
    <Paper
      className={cx(classes.rfqCard, highlighted && classes.rfqCardHighlighted)}
      elevation={0}
    >
      {/* Header: title + status + action */}
      <div className={classes.cardHeader}>
        <div
          className={classes.cardTitle}
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 600, width: '100%' }}
            noWrap
          >
            {`${supplierName} - ${rfqData.bomItemsAndActivities?.length || 0} item(s)`}
          </Typography>
          <Chip
            label={actionState.label}
            size="small"
            sx={{ justifySelf: 'flex-end' }}
            color={actionState.color}
          />
        </div>
      </div>

      {/* Body: details */}
      <div className={classes.cardBody}>
        {/* Info row: supplier, buyer, dates, bom items */}
        <div className={classes.infoRow}>
          {(supplierName || rfqData.forwardId) && (
            <Tooltip title="Supplier">
              <span className={classes.infoItem}>
                <BusinessOutlined sx={{ fontSize: 16 }} />
                <Typography variant="body2">
                  {supplierName || rfqData.forwardId}
                </Typography>
              </span>
            </Tooltip>
          )}

          {rfqData.buyerName && (
            <Tooltip title="Buyer">
              <span className={classes.infoItem}>
                <DescriptionOutlined sx={{ fontSize: 16 }} />
                <Typography variant="body2">{rfqData.buyerName}</Typography>
              </span>
            </Tooltip>
          )}

          <Tooltip title="Created">
            <span className={classes.infoItem}>
              <CalendarTodayOutlined sx={{ fontSize: 16 }} />
              <Typography variant="body2">
                {moment(rfqData.dateCreated).format('L LT')}
              </Typography>
            </span>
          </Tooltip>

          {bomItemCount > 0 && (
            <Tooltip title="BOM items">
              <span className={classes.infoItem}>
                <InventoryOutlined sx={{ fontSize: 16 }} />
                <Typography variant="body2">
                  {bomItemCount} item{bomItemCount !== 1 ? 's' : ''}
                </Typography>
              </span>
            </Tooltip>
          )}
          {hasResponse && (
            <Tooltip title="Supplier response">
              <span className={classes.infoItem}>
                <CheckCircleOutlined sx={{ fontSize: 16 }} />
                <Typography variant="body2">1 response received</Typography>
              </span>
            </Tooltip>
          )}
        </div>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {actionState.color === 'warning' || actionState.color === 'error' ? (
            <ErrorOutlineOutlined color={actionState.color} fontSize="small" />
          ) : (
            <CheckCircleOutlined
              color={getActionIconColor(actionState.color)}
              fontSize="small"
            />
          )}
          <Typography variant="body2" color="text.secondary">
            {actionState.helper}
          </Typography>
        </Box>

        {/* Activities */}
        {workingSteps.length > 0 && (
          <div className={classes.activitiesRow}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mr: 0.5 }}
            >
              Activities:
            </Typography>
            {workingSteps.map((ws) => (
              <Chip
                key={ws}
                label={getLocalizedWorkingStepName(ws)}
                size="small"
                variant="outlined"
              />
            ))}
          </div>
        )}

        {/* Original RFQ lines */}
        {bomItemCount > 0 && (
          <div className={classes.cardSection}>
            <div className={classes.cardSectionHeader}>
              <div className={classes.cardSectionTitle}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Original RFQ
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {bomItemCount} requested line{bomItemCount !== 1 ? 's' : ''}
                </Typography>
              </div>
              <div
                className={classes.expandToggle}
                onClick={() => setExpanded((v) => !v)}
              >
                <ExpandMoreOutlined
                  fontSize="small"
                  className={cx(
                    classes.expandIcon,
                    expanded && classes.expandIconOpen,
                  )}
                />
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  {expanded ? 'Hide lines' : 'Show lines'}
                </Typography>
              </div>
            </div>

            <Collapse in={expanded}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Item</TableCell>
                    <TableCell align="right">Activities</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rfqLines.map((line, index) => (
                    <ProjectRFQLine
                      key={line.bomItemPointer?.id || index}
                      bomItemPointer={line.bomItemPointer}
                      line={line}
                      index={index}
                    />
                  ))}
                </TableBody>
              </Table>
            </Collapse>
          </div>
        )}

        {/* Quotation response */}
        {hasResponse && rfqData.quotationResponseId && (
          <div className={classes.cardSection}>
            <div className={classes.cardSectionHeader}>
              <div className={classes.cardSectionTitle}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Quotation response
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Supplier pricing and buyer decision
                </Typography>
              </div>
              <Chip label="1 response" size="small" variant="outlined" />
            </div>
            <RFQResponse
              quotationResponseId={rfqData.quotationResponseId}
              rfqId={rfqData.id}
              bomItemsAndActivities={rfqData.bomItemsAndActivities}
              onActionComplete={onActionComplete}
            />
          </div>
        )}
      </div>
      <div className={classes.cardActionsRow}>
        <div className={classes.cardPrimaryAction} />
        <div className={classes.cardUtilityActions}>
          <DeleteButtonWithConfirmation
            title="Delete RFQ"
            onDelete={() => onDelete(rfqData)}
            buttonType="button"
            muiButtonProps={{
              disabled: loading[`deleteRfq-${rfqData.id}`],
              startIcon: loading[`deleteRfq-${rfqData.id}`] ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <DeleteOutlined fontSize="small" />
              ),
            }}
          />
          {canSend(rfqData.status) && (
            <Tooltip title="Send to supplier">
              <span>
                <Button
                  variant="contained"
                  size="small"
                  color="primary"
                  startIcon={
                    sendLoading ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <SendOutlined fontSize="small" />
                    )
                  }
                  disabled={sendLoading}
                  onClick={() => onSend(rfqData)}
                >
                  Send
                </Button>
              </span>
            </Tooltip>
          )}
        </div>
      </div>
    </Paper>
  )
}

function resolveSupplierName(
  rfq: RequestForQuotationDto,
  supplierMap: Record<string, string>,
): string | undefined {
  return (
    supplierMap[rfq.forwardId ?? ''] ||
    supplierMap[rfq.buyerRelationId ?? ''] ||
    supplierMap[rfq.forwardSender ?? ''] ||
    undefined
  )
}

const RFQGroupSection = ({
  group,
  showHeader,
  loading,
  onSend,
  onDelete,
  onActionComplete,
  supplierMap,
  initialRequestForQuotationId,
}: {
  group: RFQGroup
  showHeader: boolean
  loading: Record<string, boolean>
  onSend: (rfq: RequestForQuotationDto) => void
  onDelete: (rfq: RequestForQuotationDto) => void
  onActionComplete: () => Promise<void> | void
  supplierMap: Record<string, string>
  initialRequestForQuotationId?: string
}) => {
  const { classes, cx } = useStyles()
  const containsInitialRfq = group.rfqs.some(
    (rfq) => rfq.id === initialRequestForQuotationId,
  )
  const [expanded, setExpanded] = useState(true)

  useEffect(() => {
    if (containsInitialRfq) {
      setExpanded(true)
    }
  }, [containsInitialRfq])

  return (
    <div className={classes.groupSection}>
      {showHeader && (
        <div
          className={classes.groupHeader}
          onClick={() => setExpanded((v) => !v)}
        >
          <ExpandMoreOutlined
            fontSize="small"
            className={cx(
              classes.expandIcon,
              expanded && classes.expandIconOpen,
            )}
          />
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {group.label}
          </Typography>
          <Chip label={group.rfqs.length} size="small" variant="outlined" />
        </div>
      )}

      <Collapse in={!showHeader || expanded}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {group.rfqs.map((rfq) => (
            <RFQCard
              key={rfq.id}
              rfq={rfq}
              loading={loading}
              onSend={onSend}
              onDelete={onDelete}
              onActionComplete={onActionComplete}
              supplierName={resolveSupplierName(rfq, supplierMap)}
              initialExpanded={rfq.id === initialRequestForQuotationId}
              highlighted={rfq.id === initialRequestForQuotationId}
            />
          ))}
        </Box>
      </Collapse>
    </div>
  )
}

export const RequestForQuotationsDialog = (props: ManageRFQDialogProps) => {
  const { classes } = useStyles()
  const { openDialog } = useDialogManager()
  const { controller, loading } = useAppController(
    () => new ProjectRFQController(),
  )
  const { controller: suppliersController } = useAppController(
    () => new ProjectSuppliersController(),
  )

  const [rfqs, setRFQs] = useState<RequestForQuotationDto[]>([])
  const [supplierMap, setSupplierMap] = useState<Record<string, string>>({})
  const [groupBy, setGroupBy] = useClientStorage<GroupBy>(
    'manage-rfq-group-by',
    'none',
  )
  const [rfqView, setRfqView] = useClientStorage<RfqView>(
    'manage-rfq-view',
    'needsAction',
  )
  const getLocalizedWorkingStepName = useLocalizedWorkingStepLabel()

  const visibleRfqs = useMemo(() => filterRFQs(rfqs, rfqView), [rfqView, rfqs])

  const rfqCounts = useMemo(
    () => ({
      needsAction: rfqs.filter(needsBuyerAction).length,
      awaiting: rfqs.filter(isAwaitingSupplier).length,
      completed: rfqs.filter(isCompletedRFQ).length,
      all: rfqs.length,
    }),
    [rfqs],
  )

  const groups = useMemo(
    () =>
      groupRFQs(visibleRfqs, groupBy, supplierMap, getLocalizedWorkingStepName),
    [visibleRfqs, groupBy, supplierMap, getLocalizedWorkingStepName],
  )

  const ShowException = useShowException('Request for Quotations')
  const isLoading = loading['getRFQs']

  const fetchSuppliers = useCallback(async () => {
    try {
      const resp = await suppliersController.getSuppliers()
      const map: Record<string, string> = {}
      for (const s of resp?.projectSuppliers ?? []) {
        const name = s.name || s.glNumber || s.id || ''
        if (s.glNumber) {
          map[s.glNumber] = name
        }
        if (s.id) {
          map[s.id] = name
        }
      }
      setSupplierMap(map)
    } catch {
      // non-critical, we fall back to forwardId
    }
  }, [suppliersController])

  const fetchData = useCallback(async () => {
    try {
      const data = await controller.getRFQs()
      setRFQs(data.details ?? [])
    } catch (err) {
      ShowException(err)
    }
  }, [controller, ShowException])

  useEffect(() => {
    if (props.isOpen) {
      fetchData()
      fetchSuppliers()
    }
  }, [props.isOpen, fetchData, fetchSuppliers])

  const handleSendRFQ = useCallback(
    async (rfq: RequestForQuotationDto) => {
      try {
        const resp = await controller.sendRequestForQuotation({
          requestForQuotationId: rfq.id,
          forwardId: rfq.forwardId,
        })

        if (!resp.success) {
          throw new Error(resp.message || 'Failed to send RFQ')
        }
      } catch (err) {
        ShowException(err)
      } finally {
        fetchData()
      }
    },
    [controller, fetchData, ShowException],
  )

  const handleDeleteRFQ = useCallback(
    async (rfq: RequestForQuotationDto) => {
      try {
        await controller.deleteRFQ(rfq.id)
        setRFQs((prev) => prev.filter((r) => r.id !== rfq.id))
      } catch (err) {
        ShowException(err)
      } finally {
        fetchData()
      }
    },
    [controller, ShowException, fetchData],
  )

  const handleOpenDraftDialog = useCallback(() => {
    openDialog('DraftRFQDialog')
  }, [openDialog])

  return (
    <Dialog
      open={props.isOpen}
      onClose={props.onClose}
      fullWidth
      maxWidth="xxl"
      slotProps={{
        paper: { sx: { height: '90vh' } },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography variant="h6" component="span">
            Request for Quotations
          </Typography>
          <Chip
            label={`${rfqCounts.needsAction} need action`}
            size="small"
            color={rfqCounts.needsAction > 0 ? 'warning' : 'success'}
            variant="outlined"
          />
        </Box>

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <ToggleButtonGroup
            value={rfqView}
            exclusive
            onChange={(_, value) => value && setRfqView(value)}
            size="small"
          >
            <ToggleButton value="all">All</ToggleButton>
            <ToggleButton value="needsAction">Needs action</ToggleButton>
            <ToggleButton value="awaiting">Awaiting</ToggleButton>
            <ToggleButton value="completed">Completed</ToggleButton>
          </ToggleButtonGroup>
          <ToggleButtonGroup
            value={groupBy}
            exclusive
            onChange={(_, value) => value && setGroupBy(value)}
            size="small"
          >
            <ToggleButton value="none">Ungrouped</ToggleButton>
            <ToggleButton value="supplier">Supplier</ToggleButton>
            <ToggleButton value="activity">Activity</ToggleButton>
            <ToggleButton value="status">Status</ToggleButton>
          </ToggleButtonGroup>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchData} disabled={isLoading} size="small">
              {isLoading ? (
                <CircularProgress size={18} />
              ) : (
                <RefreshOutlined fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddCircleOutlined />}
            onClick={handleOpenDraftDialog}
          >
            New RFQ
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1 }}
      >
        {rfqs.length > 0 && (
          <div className={classes.summaryStrip}>
            <div className={classes.summaryCard}>
              <Typography variant="caption" color="text.secondary">
                Needs action
              </Typography>
              <div className={classes.summaryValue}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {rfqCounts.needsAction}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  RFQ{rfqCounts.needsAction === 1 ? '' : 's'} to handle
                </Typography>
              </div>
            </div>
            <div className={classes.summaryCard}>
              <Typography variant="caption" color="text.secondary">
                Awaiting supplier
              </Typography>
              <div className={classes.summaryValue}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {rfqCounts.awaiting}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  sent RFQ{rfqCounts.awaiting === 1 ? '' : 's'}
                </Typography>
              </div>
            </div>
            <div className={classes.summaryCard}>
              <Typography variant="caption" color="text.secondary">
                Accepted
              </Typography>
              <div className={classes.summaryValue}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {rfqCounts.completed}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  RFQ{rfqCounts.completed === 1 ? '' : 's'} accepted
                </Typography>
              </div>
            </div>
            <div className={classes.summaryCard}>
              <Typography variant="caption" color="text.secondary">
                Total RFQs
              </Typography>
              <div className={classes.summaryValue}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {rfqCounts.all}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  in this project
                </Typography>
              </div>
            </div>
          </div>
        )}

        {rfqs.length === 0 && !isLoading && (
          <div className={classes.emptyState}>
            <Typography variant="body1">No RFQs found</Typography>
            <Typography variant="body2">
              Create a new RFQ to get started
            </Typography>
            <Button
              variant="outlined"
              startIcon={<AddCircleOutlined />}
              onClick={handleOpenDraftDialog}
            >
              Create RFQ
            </Button>
          </div>
        )}

        {rfqs.length > 0 && visibleRfqs.length === 0 && !isLoading && (
          <div className={classes.emptyState}>
            <Typography variant="body1">No RFQs in this view</Typography>
            <Typography variant="body2">
              Switch views to see awaiting, completed, or all RFQs.
            </Typography>
          </div>
        )}

        {groups.map((group) => (
          <RFQGroupSection
            key={group.key}
            group={group}
            showHeader={groupBy !== 'none'}
            loading={loading}
            onSend={handleSendRFQ}
            onDelete={handleDeleteRFQ}
            onActionComplete={fetchData}
            supplierMap={supplierMap}
            initialRequestForQuotationId={props.initialRequestForQuotationId}
          />
        ))}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button variant="outlined" onClick={props.onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export const OpenManageRFQDialogButton = () => {
  const { openDialog } = useDialogManager()

  return (
    <Button onClick={() => openDialog('ManageRFQDialog')}>Manage RFQs</Button>
  )
}
